/* lib/adapters/auth.js — `authReal`: NON-session auth-флоу поверх боевых клиентов.
 * Сессия login/logout/me живёт в AuthProvider (useAuth) — здесь её НЕТ. Это: регистрация,
 * сброс пароля (запрос+подтверждение), верификация email, подтверждение смены email,
 * resend-verify, preview/accept приглашения. Всё через unwrap → throw{status,code,detail},
 * т.к. экраны ловят err.code/err.message в catch-ветках (контракт fakeApi сохранён).
 *
 * Расхождения backend ↔ экраны (мок), которые скрывает адаптер:
 *  • register: SignUp шлёт {name, email, password}; бэк ждёт {full_name, email, password} → name→full_name.
 *    Anti-enumeration: 201 {ok} даже если email занят (мок-ветка duplicate_email/err.field='email'
 *    больше не наступает — поле остаётся, но SignUp её просто не получит).
 *  • confirmPasswordReset: NewPassword шлёт {uid, token, password}; бэк ждёт {uid, token, new_password}.
 *  • confirmEmailChange: бэк ждёт ТОЛЬКО {uid, token} (без newEmail — новый адрес лежит в pending_email
 *    на сервере). Параметр newEmail из URL экрана игнорируем. Ответ {ok, email?}.
 *  • previewInvitation: бэк отдаёт is_authenticated/requires_register/invited_email (мок добавлял
 *    булев email_matches). Поле email_matches НЕЛЬЗЯ вывести из preview без сравнения с текущим
 *    пользователем → деривим в фасаде (см. index.js, где есть доступ к user.email). Здесь — pass-through
 *    серверной формы; преобразование email_matches делает фасад перед отдачей экрану.
 *  • Коды ошибок (invalid_token, email_mismatch, invitation_expired, invitation_revoked,
 *    already_accepted, already_member, rate_limited) прокидываются unwrap'ом как err.code — экраны
 *    показывают err.message (detail с бэка). */

import { unwrap } from '../api/unwrap.js';
import * as authApi from '../api/auth.js';
import * as profileApi from '../api/profile.js';
import * as teamApi from '../api/team.js';

// POST /api/auth/register/ — {name?|full_name?, email, password} → {ok:true} (201).
// name→full_name (SignUp шлёт name). Anti-enumeration: успех даже на занятом email.
async function register(payload) {
  const body = {
    full_name: (payload && (payload.full_name ?? payload.name)) || '',
    email: (payload && payload.email) || '',
    password: (payload && payload.password) || '',
    consent: !!(payload && payload.consent),
  };
  // Публичная ссылка покупки: адрес страницы оплаты едет в письмо подтверждения
  // (сервер примет только /buy/<слаг>, см. accounts/return_paths.py).
  if (payload && payload.next) body.next = payload.next;
  return unwrap(authApi.register(body));
}

// POST /api/auth/password/reset/ — всегда {ok:true} (anti-enumeration). Forgot/EmailSent.
async function requestPasswordReset(email) {
  return unwrap(authApi.requestPasswordReset({ email: email || '' }));
}

// POST /api/auth/resend-verify/ — всегда {ok:true} (anti-enumeration). EmailSent/SignIn.
async function resendVerify(email, next) {
  return unwrap(authApi.resendVerify({ email: email || '', ...(next ? { next } : {}) }));
}

// POST /api/auth/verify-email/ {uid, token[, next]} → {ok:true} | 400 code='invalid_token'.
// С адресом страницы покупки (`next`) сервер при первом подтверждении сразу входит:
// → {ok, logged_in:true, next, user} — VerifyEmail ведёт прямо на оплату.
async function verifyEmail(payload) {
  const body = {
    uid: (payload && payload.uid) || '',
    token: (payload && payload.token) || '',
  };
  if (payload && payload.next) body.next = payload.next;
  return unwrap(authApi.verifyEmail(body));
}

// POST /api/auth/password/reset/confirm/ — NewPassword шлёт {uid, token, password};
// бэк ждёт {uid, token, new_password}. {ok:true} | 400 code='invalid_token'.
async function confirmPasswordReset(payload) {
  return unwrap(authApi.confirmPasswordReset({
    uid: (payload && payload.uid) || '',
    token: (payload && payload.token) || '',
    new_password: (payload && (payload.new_password ?? payload.password)) || '',
  }));
}

// POST /api/auth/profile/email/change/confirm/ — бэк ждёт ТОЛЬКО {uid, token} (newEmail из URL
// игнорируем: новый адрес уже в pending_email на сервере). {ok:true, email?} | 400 code='invalid_token'.
async function confirmEmailChange(payload) {
  return unwrap(profileApi.confirmEmailChange({
    uid: (payload && payload.uid) || '',
    token: (payload && payload.token) || '',
  }));
}

// POST /api/invitations/preview/ (PUBLIC) {token} → серверная форма preview.
// 404 code='invalid_token' (приглашение не найдено) — экран ловит как previewError.
async function previewInvitation(token) {
  return unwrap(teamApi.previewInvitation(token || ''));
}

// POST /api/invitations/accept/ {token} → {workspace}. Требует сессию; переключает active ws.
// 400 invitation_expired/invitation_revoked, 403 email_mismatch, 409 already_accepted/already_member.
async function acceptInvitation(token) {
  return unwrap(teamApi.acceptInvitation(token || ''));
}

// POST /api/auth/workspaces/business/ — создать бизнес-пространство. Тело {name, restaurant?:{...}};
// бэк отдаёт {workspace} (201, is_active:false, дефолтный Free через signal). Возвращаем сам workspace.
// 400 code='name' (пустое имя). BusinessUpgradeShared ловит err.detail.
async function createBusinessWorkspace(payload) {
  const d = await unwrap(authApi.createBusinessWorkspace({
    name: (payload && payload.name) || '',
    restaurant: (payload && payload.restaurant) || undefined,
  }));
  return d.workspace || d;
}

// PATCH /api/auth/workspaces/{id}/ {name} — переименовать (admin-only). {workspace} | 403 (не админ) / 404 (не член).
async function renameWorkspace(id, name) {
  const d = await unwrap(authApi.renameWorkspace(id, name));
  return d.workspace || d;
}

// PATCH /api/auth/workspaces/{id}/ {archived:true} — архивировать (owner-only). {workspace} |
// 403 code='not_owner' / 400 code='cannot_archive_personal'. Архивный ws исчезает из списка.
async function archiveWorkspace(id) {
  const d = await unwrap(authApi.archiveWorkspace(id));
  return d.workspace || d;
}

export const authReal = {
  register, requestPasswordReset, resendVerify, verifyEmail,
  confirmPasswordReset, confirmEmailChange, previewInvitation, acceptInvitation,
  createBusinessWorkspace, renameWorkspace, archiveWorkspace,
};
